import _ from "lodash";
import {getFilteredTreeWithPaths, getMappedTree, getNodes} from "./tree";

export const CATALOG_RESOURCE_TYPE_DATASET = "stat"; // this constant does not represent a real resource type, it is used internally only
export const CATALOG_RESOURCE_TYPE_DASHBOARD_WIDGET = "dashboardwidget";
export const CATALOG_RESOURCE_TYPE_METADATA_URL = "exportablemetadata";
export const CATALOG_RESOURCE_TYPE_EXTERNAL_DATA_URL = "dataurl";

export const CATALOG_RESOURCE_TYPE_ODATA = "odata";
export const CATALOG_RESOURCE_TYPE_CSV = "csv";
export const CATALOG_RESOURCE_TYPE_GEOJSON = "geojson";
export const CATALOG_RESOURCE_TYPE_WMS = "wms";
export const CATALOG_RESOURCE_TYPE_WMTS = "wmts";
export const CATALOG_RESOURCE_TYPE_ZIP = "zip";
export const CATALOG_RESOURCE_TYPE_GZ = "gz";
export const CATALOG_RESOURCE_TYPE_GZIP = "gzip";
export const CATALOG_RESOURCE_TYPE_7Z = "7z";

export const CATALOG_PACKAGE_TYPE_DASHBOARD = "dashboard";

/** catalog generation **/

export const CATEGORY_PATH_SEPARATOR = "+";

export const getCatalog = (serverCatalog = {}, showSingleCategoryScheme = false, t) => {
  const categoryGroups = getDecoratedCategoryGroups(serverCatalog.categoryGroups, serverCatalog.packageMap);
  const categoryPathLabels = getCategoryPathLabels(categoryGroups);
  const hasCategorySchemes = categoryGroups.length > (showSingleCategoryScheme ? 0 : 1);

  const packageMap = getDecoratedPackageMap(serverCatalog.packageMap, categoryGroups);

  const isEmpty = Object.keys(packageMap).length === 0;

  const resourceMap = getDecoratedResourceMap(serverCatalog.resourceMap);

  const detailLevelTree = getDetailLevelTree(serverCatalog.catalogLayers, t);
  const detailLevels = getNodes(detailLevelTree, "layers", dl => dl.catalogSelection).sort(
    (a, b) => a.order - b.order || a.id - b.id
  );

  return {
    categoryGroups: categoryGroups,
    categoryPathLabels: categoryPathLabels,
    hasCategorySchemes: hasCategorySchemes,
    packageMap: packageMap,
    uncategorizedPackages: serverCatalog.uncategorizedPackages ?? [],
    resourceMap: resourceMap,
    datasetMap: serverCatalog.datasetMap ?? {},
    isEmpty: isEmpty,
    detailLevelTree: detailLevelTree,
    detailLevels: detailLevels
  };
};

const getDecoratedCategoryGroups = (categoryGroups, packageMap) => {
  const newCategoryGroups = (categoryGroups || []).map(group => {
    const groudId = group.id;
    return {
      ...group,
      path: groudId,
      categories: getDecoratedCategories(group.categories, groudId, packageMap)
    };
  });

  return newCategoryGroups;
};

const getDecoratedCategories = (categories, parentPath, packageMap) => {
  const newCategories = (categories || []).map(category => {
    const categorypath = parentPath + CATEGORY_PATH_SEPARATOR + category.id;
    return {
      ...category,
      path: categorypath,
      childrenCategories: getDecoratedCategories(category.childrenCategories, categorypath, packageMap)
    };
  });

  return newCategories;
};

const getDecoratedPackageMap = (packageMap, categoryGroups) => {
  const newPackageMap = packageMap ? _.cloneDeep(packageMap) : {};

  // recursive function to explore categories and assign categoryIds directly to datasets
  const processCategory = category => {
    if (category.packageIdentifiers) {
      category.packageIdentifiers.forEach(packageId => {
        if (newPackageMap[packageId]) {
          // adding category ids to dataset
          if (!newPackageMap[packageId].categoryIds) {
            newPackageMap[packageId].categoryIds = [];
          }
          if (!(newPackageMap[packageId].categoryIds || []).includes(category.id)) {
            newPackageMap[packageId].categoryIds.push(category.id);
          }
          // adding category paths to dataset
          if (!newPackageMap[packageId].categoryPaths) {
            newPackageMap[packageId].categoryPaths = [];
          }
          if (!(newPackageMap[packageId].categoryPaths || []).includes(category.path)) {
            newPackageMap[packageId].categoryPaths.push(category.path);
          }
        }
      });
    }

    // processes subcategories recursively
    if (category.childrenCategories) {
      category.childrenCategories.forEach(childCategory => processCategory(childCategory));
    }
  };

  // iterate on all categoryGroups and categories to update packageMap
  (categoryGroups || []).forEach(group => {
    group.categories.forEach(category => {
      processCategory(category);
    });
  });

  return newPackageMap;
};

const extractFormatFromUrl = url => {
  if (!url || typeof url !== "string") {
    // Invalid input
    return null;
  }

  // Strip query/hash
  const cleanUrl = url.split(/[?#]/)[0];

  // Extract filename from last path segment
  const filename = cleanUrl.split("/").pop();

  if (!filename || !filename.includes(".")) {
    // No extension present
    return null;
  }

  // Normalize extension
  const ext = filename.split(".").pop().toLowerCase();

  return ext || null;
};

const getDecoratedResourceMap = resourceMap => {
  const newResourceMap = {};

  Object.keys(resourceMap ?? {}).forEach(resourceId => {
    const resource = resourceMap[resourceId];

    let newType = (resource?.type || "").toLowerCase();

    if (newType === "json") {
      const resourceUrl = resource?.extensions?.url;
      const resourceFormat = extractFormatFromUrl(resourceUrl);
      if (resourceFormat) {
        newType = resourceFormat;
      }
    } else if (newType === "wms_srvc") {
      newType = CATALOG_RESOURCE_TYPE_WMS;
    }

    newResourceMap[resourceId] = {
      ...resource,
      type: newType
    };
  });

  return newResourceMap;
};

const getCategoryPathLabels = categoryGroups => {
  const categoryPathLabels = {};

  const processCategory = category => {
    categoryPathLabels[category.path] = category.label;

    // processes subcategories recursively
    if (category.childrenCategories) {
      category.childrenCategories.forEach(childCategory => processCategory(childCategory));
    }
  };

  // iterate on all categoryGroups and categories to update packageMap
  (categoryGroups || []).forEach(group => {
    group.categories.forEach(category => {
      processCategory(category);
    });
  });

  return categoryPathLabels;
};

/** catalog utilities **/

export const getFilteredCatalog = (catalog, filter) => {
  const filteredPackageMap = {};
  Object.keys(catalog.packageMap)
    .filter(id => filter(id, catalog.packageMap[id]))
    .forEach(id => (filteredPackageMap[id] = catalog.packageMap[id]));

  const categoryGroups = (catalog.categoryGroups || [])
    .map(group => ({
      ...group,
      categories: getFilteredTreeWithPaths(
        getMappedTree(group.categories, "childrenCategories", cat => ({
          ...cat,
          packageIdentifiers: (cat.packageIdentifiers || []).filter(
            datasetId => filteredPackageMap[datasetId] !== undefined
          )
        })),
        "childrenCategories",
        cat => cat.packageIdentifiers && cat.packageIdentifiers.length > 0
      )
    }))
    .filter(group => group.categories && group.categories.length > 0);

  const uncategorizedPackages = (catalog.uncategorizedPackages || []).filter(
    datasetId => filteredPackageMap[datasetId] !== undefined
  );

  return {
    ...catalog,
    categoryGroups: categoryGroups,
    datasets: filteredPackageMap,
    uncategorizedPackages: uncategorizedPackages,
    isEmpty: Object.keys(filteredPackageMap).length === 0
  };
};

export const getCategoryFromCatalog = (categoryPath, catalog) => {
  if (!categoryPath || !catalog) {
    return null;
  }

  let res = null;

  if (catalog.categoryGroups.length === 0) {
    return null;
  } else if (catalog.categoryGroups.length === 1) {
    const category = (catalog.categoryGroups[0]?.categories || []).find(category => category.id === categoryPath[0]);

    if (!category) {
      return null;
    }

    if (categoryPath.length === 1) {
      res = category;
    } else {
      let tree = category.childrenCategories;
      categoryPath.slice(1).forEach((elem, idx, arr) => {
        if (idx < arr.length - 1) {
          tree = tree.find(({id}) => id === elem)?.childrenCategories || [];
        } else {
          res = tree.find(({id}) => id === elem) || null;
        }
      });
    }
  } else {
    const categorySchema = catalog.categoryGroups.find(categoryGroup => categoryGroup.id === categoryPath[0]);

    if (!categorySchema) {
      return null;
    }

    if (categoryPath.length === 1) {
      res = {
        childrenCategories: categorySchema.categories,
        packageIdentifiers: []
      };
    } else {
      let tree = categorySchema.categories;
      categoryPath.slice(1).forEach((elem, idx, arr) => {
        if (idx < arr.length - 1) {
          tree = tree.find(({id}) => id === elem)?.childrenCategories || [];
        } else {
          res = tree.find(({id}) => id === elem) || null;
        }
      });
    }
  }

  return res;
};

export const getDatasetFromCatalog = (packageId, catalog) => {
  const catalogPackage = catalog.packageMap[packageId];
  if (!catalogPackage) {
    return null;
  }

  const catalogPackageDatasetId = catalogPackage.dataflowIdentifiers?.[0] || null;
  if (!catalogPackageDatasetId) {
    return null;
  }

  const catalogDataset = catalog.datasetMap[catalogPackageDatasetId];
  if (!catalogDataset) {
    return null;
  }

  let type = CATALOG_RESOURCE_TYPE_DATASET;
  let metadataUrl = null;
  let dashboardUrl = null;
  let externalDataUrl = null;
  const attachedFiles = [];

  catalogPackage.resourceIdentifiers.forEach(resourceId => {
    const resource = catalog.resourceMap[resourceId];
    const resourceType = (resource?.type || "").toLowerCase();

    if (resourceType === CATALOG_RESOURCE_TYPE_METADATA_URL) {
      metadataUrl = resource.extensions.url ?? "";
    } else if (resourceType === CATALOG_RESOURCE_TYPE_DASHBOARD_WIDGET) {
      if (resource.extensions?.url !== null && resource.extensions?.url !== undefined) {
        dashboardUrl = resource.extensions.url;
        type = CATALOG_PACKAGE_TYPE_DASHBOARD;
      }
    } else if (resourceType === CATALOG_RESOURCE_TYPE_EXTERNAL_DATA_URL) {
      if (resource.extensions?.url !== null && resource.extensions?.url !== undefined) {
        externalDataUrl = resource.extensions.url;
      }
    } else {
      if (resource.extensions?.url !== null && resource.extensions?.url !== undefined) {
        attachedFiles.push({
          format: resourceType,
          url: resource.extensions.url
        });
      }
    }
  });

  const dataset = {
    ...catalogPackage,
    ...catalogDataset,
    id: catalogPackageDatasetId,
    datasetId: catalogPackageDatasetId,
    type: type,
    attachedFiles: attachedFiles,
    metadataUrl: metadataUrl,
    dashboardUrl: dashboardUrl,
    externalDataUrl: externalDataUrl,
    detailLevels: [] // TODO!
  };

  return dataset;
};

export const getDecoratedResource = (resource, resourceId) => {
  return resource
    ? {
        ...resource,
        id: resourceId,
        type: (resource.type ?? "").toLowerCase(),
        ...resource.extensions,
        extensions: undefined
      }
    : null;
};

export const getResourceFromCatalogByType = (catalog, packageId, resourceType) => {
  const catalogPackage = catalog?.packageMap?.[packageId];
  if (!catalogPackage || !catalogPackage?.resourceIdentifiers) {
    return null;
  }

  if (resourceType === CATALOG_RESOURCE_TYPE_DATASET) {
    return getDatasetFromCatalog(packageId, catalog);
  }

  const resourceId = (catalogPackage?.resourceIdentifiers || []).find(id => {
    const resource = catalog?.resourceMap?.[id];
    if (!resource || resource.unavailable) return false;
    return (resource.type ?? "").toLowerCase() === resourceType.toLowerCase();
  });

  if (!resourceId) {
    return null;
  }

  const resource = catalog?.resourceMap?.[resourceId];

  return getDecoratedResource(resource, resourceId);
};

export const getResourceFromCatalogById = (catalog, resourceId) => {
  const resource = catalog?.resourceMap?.[resourceId];
  if (!resource || resource.unavailable) return null;
  return getDecoratedResource(resource, resourceId);
};

export const getPackageExternalResources = (packageId, catalog) => {
  const externalResources = [];

  const catalogPackage = catalog.packageMap[packageId];
  if (!catalogPackage) {
    return [];
  }

  catalogPackage.resourceIdentifiers.forEach(resourceId => {
    const resource = catalog.resourceMap[resourceId];
    const resourceType = (resource?.type || "").toLowerCase();

    if (
      resource &&
      resourceType !== CATALOG_RESOURCE_TYPE_METADATA_URL &&
      resourceType !== CATALOG_RESOURCE_TYPE_DASHBOARD_WIDGET &&
      resourceType !== CATALOG_RESOURCE_TYPE_EXTERNAL_DATA_URL &&
      resourceType !== CATALOG_RESOURCE_TYPE_WMS &&
      resourceType !== CATALOG_RESOURCE_TYPE_WMTS &&
      resourceType !== CATALOG_RESOURCE_TYPE_ODATA
    ) {
      externalResources.push({
        id: resourceId,
        format: resource.type,
        url: resource.extensions?.url,
        title:
          resource.type === "html" || !resource.extensions?.url
            ? resource.title
            : resource.extensions.url.split("/").pop()
      });
    }
  });

  return externalResources;
};

export const convertCategoryIdListToPathList = (categoryIds = []) => {
  return categoryIds.map((_, idx) => categoryIds.slice(0, idx + 1).join(CATEGORY_PATH_SEPARATOR));
};

/** detail levels **/

export const getDetailLevelTree = (tree, t) =>
  tree
    ? getMappedTree(tree, "layers", (node, idx) => ({
        ...node,
        key: (node.id || `root_${idx}`).toString(),
        label: node.parentCategory
          ? node.label || node.nameIdentifier
          : t(`detailLevels.categories.${node.category}`) /* i18next-extract-disable-line */,
        layers: (node.layers || [])
          .map(child => ({...child, parentCategory: node.category || null}))
          .sort((a, b) => a.order - b.order || a.id - b.id)
      }))
    : [];

export const getDetailLevelFromTree = (tree, prefValues = null) => {
  const values = getNodes(tree, "layers", node => node.id !== null && node.id !== undefined).map(({id}) => id);
  if (values.length > 0) {
    if (prefValues !== null) {
      const prefValue = prefValues.find(value => values.includes(value));
      if (prefValue) {
        return prefValue;
      }
    }

    if (values.includes(3)) {
      return 3;
    } else {
      return values[0];
    }
  }
};
